<template>
  <div class="work-time-editor">
    <div class="working-time-row">
      <span class="working-time-row__label">労働時間</span>
      <span class="d-flex align-center">
        <span class="working-time-row__value">{{ formattedWorkingTime }}</span>
        <v-btn
          x-small
          icon
          :ripple="false"
          class="ml-1"
          @click="toggleEditing"
        >
          <v-icon x-small>
            {{ isEditing ? icons.mdiChevronUp : icons.mdiPencilOutline }}
          </v-icon>
        </v-btn>
      </span>
    </div>

    <v-expand-transition>
      <div
        v-if="isEditing"
        class="work-time-editor__form mb-4"
      >
        <v-skeleton-loader
          v-if="isLoading"
          type="list-item, list-item"
        />

        <template v-else>
          <v-alert
            v-if="timecards.length"
            dense
            text
            type="info"
            class="text-caption mb-2"
          >
            保存した変更を集計値へ反映するには再集計を実行してください
          </v-alert>

          <div
            v-if="!timecards.length"
            class="text-center secondary--text text-caption py-2"
          >
            タイムカードがありません
          </div>

          <div
            v-for="timecard in timecards"
            :key="`timecard-${timecard.id}`"
            class="work-time-editor__timecard"
          >
            <!-- 入力は時刻(HH:mm)のみ。日付部分は useTimecardBusinessDate が
                 営業日ウィンドウに収まるよう自動解決する(開始=openAt境界 /
                 終了=開始基準。データセット管理の勤怠テーブルと同じルール)。 -->
            <div class="work-time-editor__time-fields">
              <div class="work-time-editor__time-field">
                <div class="work-time-editor__time-label">
                  開始
                </div>
                <timecard-date-time-cell
                  :value="timecard.startAt"
                  :business-date="date"
                  :error="isInvalidWorkingDuration(timecard.startAt, timecard.leaveAt)"
                  @input="timecard.startAt = $event"
                  @commit="resolveTimecardDates(timecard)"
                />
              </div>
              <div class="work-time-editor__time-field">
                <div class="work-time-editor__time-label">
                  終了
                </div>
                <timecard-date-time-cell
                  :value="timecard.leaveAt"
                  :business-date="date"
                  :error="isInvalidWorkingDuration(timecard.startAt, timecard.leaveAt)"
                  @input="timecard.leaveAt = $event"
                  @commit="resolveTimecardDates(timecard)"
                />
              </div>
            </div>
            <v-btn
              block
              small
              color="primary"
              class="mt-2"
              :loading="savingTimecardId === timecard.id"
              :disabled="!isDirty(timecard) || isInvalidWorkingDuration(timecard.startAt, timecard.leaveAt) || savingTimecardId !== null"
              @click="save(timecard)"
            >
              保存
            </v-btn>
          </div>
        </template>
      </div>
    </v-expand-transition>
  </div>
</template>

<script>
import {
  ref,
  computed,
  watch,
  toRefs,
  getCurrentInstance,
} from '@vue/composition-api'
import { parseISO } from 'date-fns'
import { mdiPencilOutline, mdiChevronUp } from '@mdi/js'
import TimecardApi from '@/api/v2/eod-closing/Timecard'
import useTimecardBusinessDate from '@/views/composable/useTimecardBusinessDate'
import TimecardDateTimeCell from '@/views/v2-temp/eod-closing-v2/components/TimecardDateTimeCell.vue'
import { handleApiError } from '@/utils/errorHandler'

export default {
  name: 'WorkTimeEditor',
  components: {
    TimecardDateTimeCell,
  },
  props: {
    date: {
      type: String,
      default: '',
    },
    userId: {
      type: Number,
      default: null,
    },
    workingMinutes: {
      type: Number,
      default: 0,
    },
  },
  setup(props, { emit }) {
    const vm = getCurrentInstance().proxy
    const { date } = toRefs(props)

    const isEditing = ref(false)
    const isLoading = ref(false)
    const savingTimecardId = ref(null)
    const timecards = ref([])
    const originalsById = ref({})

    // HH:mm入力の日付部分を営業日ウィンドウに収まるよう解決する
    // (開始=openAt境界 / 終了=開始基準。データセット管理の勤怠テーブルと同じルール)
    const { resolveStartAt, resolveLeaveAt } = useTimecardBusinessDate(date)

    // 時刻確定時に開始・終了両方の日付を再解決する(終了は開始の日付に依存するため)
    const resolveTimecardDates = timecard => {
      const resolvedStart = resolveStartAt(timecard.startAt)
      if (resolvedStart !== timecard.startAt) {
        // eslint-disable-next-line no-param-reassign
        timecard.startAt = resolvedStart
      }
      const resolvedLeave = resolveLeaveAt(timecard.startAt, timecard.leaveAt)
      if (resolvedLeave !== timecard.leaveAt) {
        // eslint-disable-next-line no-param-reassign
        timecard.leaveAt = resolvedLeave
      }
    }

    // 労働時間（分単位の整数）を整形する。
    // - 時間 == 0 → 「M分」 / 分 == 0 → 「N時間」 / それ以外 → 「N時間MM分」(分は2桁ゼロ埋め)
    const formatWorkingMinutes = minutes => {
      const total = Number(minutes || 0)
      const hours = Math.floor(total / 60)
      const mins = total % 60

      if (hours === 0) return `${mins}分`
      if (mins === 0) return `${hours}時間`

      return `${hours}時間${String(mins).padStart(2, '0')}分`
    }

    const formattedWorkingTime = computed(() => formatWorkingMinutes(props.workingMinutes))

    const isEndBeforeStart = (start, end) => {
      const startDate = parseISO(start)
      const endDate = parseISO(end)
      if ([startDate, endDate].some(incomingDate => Number.isNaN(incomingDate.getTime()))) return true

      return endDate < startDate
    }

    const isInvalidWorkingDuration = (startAt, leaveAt) => {
      if (!startAt) return true // 開始は必須
      if (!leaveAt) return false // 終了だけない=>OK（勤務中）

      return isEndBeforeStart(startAt, leaveAt)
    }

    const fetchTimecards = async () => {
      if (!props.date || !props.userId) return

      isLoading.value = true
      try {
        const res = await TimecardApi.getTimecards(props.date, { userId: props.userId })
        if (res?.data?.status === 'success') {
          const data = res.data.data.data || []
          timecards.value = data.map(timecard => ({
            id: timecard.attributes.id,
            startAt: timecard.attributes.startAt,
            leaveAt: timecard.attributes.leaveAt,
          }))
          originalsById.value = Object.fromEntries(
            timecards.value.map(timecard => [
              timecard.id,
              { startAt: timecard.startAt, leaveAt: timecard.leaveAt },
            ]),
          )
        }
      } catch (error) {
        handleApiError(error, { $toast: vm.$toast })
      } finally {
        isLoading.value = false
      }
    }

    const toggleEditing = () => {
      isEditing.value = !isEditing.value
      if (isEditing.value) fetchTimecards()
    }

    const isDirty = timecard => {
      const original = originalsById.value[timecard.id]
      if (!original) return false

      return timecard.startAt !== original.startAt || timecard.leaveAt !== original.leaveAt
    }

    const save = async timecard => {
      savingTimecardId.value = timecard.id
      try {
        resolveTimecardDates(timecard)
        const original = originalsById.value[timecard.id] || {}
        const params = {}
        if (timecard.startAt !== original.startAt) params.startAt = timecard.startAt
        // 終了未入力のまま(=勤務中)は leaveAt を送らず現状維持（nil化はタイムカードテーブルで実施）
        if (timecard.leaveAt && timecard.leaveAt !== original.leaveAt) params.leaveAt = timecard.leaveAt

        const res = await TimecardApi.updateTimecard(timecard.id, params)
        if (res?.data?.status === 'success') {
          vm.$toast.success('勤務時間を保存しました')
          await fetchTimecards()
          emit('saved')
        } else if (res?.data?.status === 'error') {
          const { message } = res.data
          const messages = Array.isArray(message) ? message : [message]
          messages.forEach(msg => vm.$toast.error(msg))
        }
      } catch (error) {
        handleApiError(error, { $toast: vm.$toast })
      } finally {
        savingTimecardId.value = null
      }
    }

    // ユーザー・日付が変わったら編集状態をリセット
    watch([() => props.userId, () => props.date], () => {
      isEditing.value = false
      timecards.value = []
      originalsById.value = {}
    })

    return {
      isEditing,
      isLoading,
      savingTimecardId,
      timecards,
      formattedWorkingTime,
      isInvalidWorkingDuration,
      toggleEditing,
      resolveTimecardDates,
      isDirty,
      save,
      icons: {
        mdiPencilOutline,
        mdiChevronUp,
      },
    }
  },
}
</script>

<style lang="scss" scoped>
.working-time-row {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 8px 0;
  border-bottom: 1px solid rgba(150, 150, 150, 0.2);
  margin-bottom: 16px;

  &__label {
    font-size: 12px;
    color: var(--v-secondary-base, #8a8f9e);
  }

  &__value {
    font-size: 14px;
    font-weight: 600;
    font-variant-numeric: tabular-nums;
  }
}

.work-time-editor__form {
  margin-top: -8px;
}

.work-time-editor__timecard {
  padding: 8px 0 12px;

  &:not(:last-child) {
    border-bottom: 1px dashed rgba(150, 150, 150, 0.18);
  }
}

.work-time-editor__time-fields {
  display: flex;
  gap: 12px;
}

.work-time-editor__time-field {
  flex: 1;
  min-width: 0;
}

.work-time-editor__time-label {
  font-size: 11px;
  color: var(--v-secondary-base, #8a8f9e);
  margin-bottom: 2px;
}
</style>
